import { c, card } from '../../theme';
import { chart } from '../../theme';
import { fmtCount } from './format';

// What people called about: the window's wrap-up codes, biggest first, as the
// old Board's donut.
//
// The ring is drawn with one conic gradient rather than a charting library —
// it is a single series of shares that add to a whole, which is all a donut
// ever is, and the page already carries a chart dependency it does not need a
// second use of.
//
// Colour comes from chart.coVisible, NOT chart.categorical: every slice is
// compared with every other here, and the 7-slot categorical set fails
// all-pairs testing (two hues 1.3 ΔE apart under deuteranopia). Past those
// four, slices fall to neutrals — the ranking and the labels carry the
// meaning at that point, not the hue.
const RING = [...chart.coVisible, chart.other, '#94A3B8', '#B6BEC9', '#CBD2DA', '#DDE2E8'];
const colorOf = (i) => RING[Math.min(i, RING.length - 1)];

// `title`, `centerLabel` and `footnote` are parameters rather than the fixed
// strings they were, because the Repeat Callers page draws the same shape over a
// different population: the wrap-up codes of calls from numbers that called
// again, not the window's whole Handled. The defaults are the Board's own
// wording, so its call site is unchanged and stays the one that reads as this
// component's home.
export default function VolumeDrivers({
  drivers, other, total, rangeLabel,
  title = 'Top drivers of volume',
  centerLabel = 'Total',
  footnote,
}) {
  const list = drivers || [];
  if (!list.length) {
    return (
      <section style={s.card} aria-label={title}>
        <h2 style={s.h2}>{title}</h2>
        <p style={s.empty}>No wrap-up codes stored for this window.</p>
      </section>
    );
  }
  const slices = [...list, ...(other ? [{ name: 'Everything else', ...other }] : [])];
  let at = 0;
  const stops = slices.map((d, i) => {
    const from = at;
    at += d.sharePct || 0;
    return `${colorOf(i)} ${from}% ${Math.min(at, 100)}%`;
  });

  return (
    <section style={s.card} aria-label={title}>
      <h2 style={s.h2}>{title} <span style={s.sub}>({rangeLabel})</span></h2>
      <div style={s.body}>
        <div style={s.ringWrap}>
          <div style={{ ...s.ring, background: `conic-gradient(${stops.join(', ')})` }}
            role="img" aria-label={slices.map((d) => `${d.name} ${d.sharePct}%`).join(', ')} />
          <div style={s.hole}>
            <div style={s.holeValue}>{fmtCount(total)}</div>
            <div style={s.holeLabel}>{centerLabel}</div>
          </div>
        </div>
        <ul style={s.legend}>
          {slices.map((d, i) => (
            <li key={d.name} style={s.legendRow}>
              <span style={{ ...s.dot, background: colorOf(i) }} />
              <span style={s.legendName} title={d.name}>{d.name}</span>
              <span style={s.legendNum}>{fmtCount(d.volume)}</span>
              <span style={s.legendPct}>{d.sharePct == null ? '—' : `${d.sharePct}%`}</span>
            </li>
          ))}
        </ul>
      </div>
      <p style={s.foot}>
        {footnote || (
          <>
            Voice, VIP and Callback calls an agent handled, by the wrap-up code left. Codes that are not a
            reason for calling (timeout, No Answer, No Message Left, the default code) are left out, and each
            share is of the calls with a real code. The centre is the window’s Handled.
          </>
        )}
      </p>
    </section>
  );
}

const s = {
  card:       { ...card.base, padding: '14px 18px 12px' },
  h2:         { fontSize: '13px', fontWeight: 650, color: c.text, margin: '0 0 10px' },
  sub:        { fontWeight: 400, color: c.textMuted },
  body:       { display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' },
  // The ring was a fixed 148px, which left it small beside a thirteen-row
  // legend — the chart is the thing being read, and it was the smaller half
  // of the panel. It is now sized as a share of the space with a cap rather
  // than another fixed number: this panel sits in an auto-fit grid that can
  // hand it anything from ~300px to half a wide screen, and a fixed width
  // either overflows the narrow end or wastes the wide one.
  ringWrap:   { position: 'relative', width: 'min(260px, 100%)', aspectRatio: '1', flexShrink: 0 },
  ring:       { width: '100%', height: '100%', borderRadius: '50%' },
  // A percentage inset, so the ring keeps its thickness as it scales. At the
  // old 148px the hole was 30px in, which is the 20% kept here.
  hole:       { position: 'absolute', inset: '20%', borderRadius: '50%', background: c.surface,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  holeValue:  { fontSize: '28px', fontWeight: 700, color: c.textStrong, fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.1 },
  holeLabel:  { fontSize: '11px', color: c.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em',
    marginTop: '2px' },
  legend:     { listStyle: 'none', margin: 0, padding: 0, flex: 1, minWidth: '210px' },
  legendRow:  { display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0', fontSize: '12px' },
  dot:        { width: '9px', height: '9px', borderRadius: '2px', flexShrink: 0 },
  legendName: { flex: 1, color: c.textStrong, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  legendNum:  { color: c.textStrong, fontVariantNumeric: 'tabular-nums' },
  legendPct:  { color: c.textMuted, width: '46px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  empty:      { fontSize: '12px', color: c.textMuted, margin: 0 },
  foot:       { fontSize: '11px', color: c.textMuted, margin: '10px 0 0', lineHeight: 1.45 },
};
